import { useState, useRef, useEffect } from "react";
import { useGSAP } from "@gsap/react";
import styles from "./ImageSlider.module.css";

/**
 * ImageSolo affiche une image unique avec des contrôles de taille optimaux.
 * 
 * Le composant adapte l'image pour s'intégrer parfaitement dans les modales 
 * en préservant ses proportions et en évitant qu'elle ne dépasse son conteneur.
 *
 * @param {Object} params - Les paramètres pour le composant ImageSolo.
 * @param {Object} params.images - Un objet contenant la source de l'image et son texte alternatif.
 * @param {string} params.images.src - L'URL de l'image à afficher.
 * @param {string} params.images.alt - Le texte alternatif de l'image.
 * @param {string} [params.images.caption] - Légende optionnelle pour l'image.
 * @param {boolean} [params.adaptiveHeight] - Si true, la hauteur s'adapte au contenu.
 * @return {JSX.Element} Le composant ImageSolo rendu.
 */
export default function ImageSolo({ images, adaptiveHeight = false }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0 });
  const imgSrc = useRef(images.src);
  const containerRef = useRef(null);

  // Réinitialiser l'état de chargement si la source d'image change
  useEffect(() => {
    if (imgSrc.current !== images.src) {
      setIsLoaded(false);
      imgSrc.current = images.src;
    }
  }, [images.src]);

  const handleImageLoad = (e) => {
    setIsLoaded(true);
    // Stocker les dimensions naturelles de l'image
    setImgDimensions({
      width: e.target.naturalWidth,
      height: e.target.naturalHeight,
    });
  };

  // Déterminer le style du conteneur en fonction des dimensions de l'image
  const getContainerStyle = () => {
    if (!adaptiveHeight || !isLoaded) return {};

    // Limiter la hauteur maximale pour les images très hautes
    const maxHeight = Math.min(imgDimensions.height, 300);

    return {
      height: isLoaded ? 'auto' : undefined,
      maxHeight: maxHeight > 0 ? `${maxHeight}px` : undefined,
    };
  };

  // Déterminer le style de l'image
  const getImageStyle = () => {
    return {
      maxWidth: '100%',
      maxHeight: adaptiveHeight ? '300px' : '100%',
      objectFit: 'contain',
      boxSizing: 'border-box'
    };
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.slider} ${styles.soloSlider}`}
      style={getContainerStyle()}
    >
      <div
        className={`${styles.slide} ${styles.soloSlide}`}
        style={{
          height: adaptiveHeight ? 'auto' : undefined,
          minHeight: adaptiveHeight ? '100px' : undefined,
          maxHeight: '300px',
          overflow: 'hidden'
        }}
      >
        {!isLoaded && <div className={styles.imagePlaceholder}></div>}
        <img
          src={images.src}
          alt={images.alt || "Image"}
          className={`${styles.imageSlide} ${styles.showed} ${isLoaded ? styles.loaded : ""}`}
          onLoad={handleImageLoad}
          style={getImageStyle()}
        />
      </div>

      {images.caption && (
        <div className={styles.imageCaption}>
          {images.caption}
        </div>
      )}
    </div>
  );
}
